import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/layout/SEO';
import { MaterialIcon } from '@/components/ui/MaterialIcon';
import { OAuthProviderIcon } from '@/components/auth/OAuthProviderIcon';
import { useAuth } from '@/context/AuthContext';
import { useGame } from '@/context/GameContext';

const SYSTEM_NAV = [
    { label: '🏠 Base Camp', path: '/dashboard', icon: 'home' },
    { label: 'Explore', path: '/courses', icon: 'explore' },
    { label: 'Armory', path: '/concepts', icon: 'military_tech' },
    { label: 'System', path: '/profile/settings', icon: 'settings' },
] as const;

type ThemeOption = {
    label: string;
    meta: string;
    swatch: string;
    accent: string;
    active?: boolean;
};

const THEME_OPTIONS: ThemeOption[] = [
    { label: 'Cyber Blue', meta: 'ACTIVE_PROTOCOL', swatch: '#00cffc', accent: '#00cffc', active: true },
    { label: 'Neon Green', meta: 'HUD_FILTER_02', swatch: '#39ff14', accent: '#39ff14' },
    { label: 'Molten Orange', meta: 'HUD_FILTER_03', swatch: '#ff4d00', accent: '#ff4d00' },
    { label: 'Phantom Purple', meta: 'HUD_FILTER_04', swatch: '#bc13fe', accent: '#bc13fe' },
] ;

const PROVIDERS = [
    { provider: 'github', label: 'GitHub Protocol' },
    { provider: 'google', label: 'Google Auth-Node' },
    { provider: 'discord', label: 'Discord Relay' },
] as const;

export default function SettingsPage() {
    const { user } = useAuth();
    const { stats } = useGame();

    if (!user) {
        return (
            <div className="min-h-screen bg-[#0e0e0e] px-6 py-24 text-white">
                <SEO title="STARTZONE.DEV // SYSTEM_CONFIG" />
                <div className="mx-auto flex max-w-3xl flex-col items-center justify-center text-center">
                    <div className="mb-10 flex h-32 w-32 items-center justify-center rounded-full border border-white/10 bg-[#131313] text-5xl opacity-60">
                        <MaterialIcon icon="settings" size={72} className="text-[#8cacff]" />
                    </div>
                    <h1 className="mb-6 font-['Space_Grotesk'] text-5xl font-black italic uppercase tracking-tighter">System Access Required</h1>
                    <p className="mb-12 max-w-md text-lg leading-relaxed text-[#acabaa]">
                        Authenticate before you modify identity links, HUD preferences, or purge protocols.
                    </p>
                    <Link
                        to="/"
                        className="bg-gradient-to-r from-[#8cacff] to-[#769dff] px-10 py-4 font-['Space_Grotesk'] text-sm font-black uppercase tracking-[0.2em] text-[#001f55] transition-all hover:brightness-110 active:scale-95"
                    >
                        Return To Base
                    </Link>
                </div>
            </div>
        );
    }

    const totalXp = stats?.xp ?? 0;
    const level = stats?.level ?? 1;
    const operatorId = user.provider_id.slice(0, 12).toUpperCase();

    return (
        <div className="min-h-screen bg-[#0e0e0e] text-white">
            <SEO
                title="STARTZONE.DEV // SYSTEM_SETTINGS"
                description="System settings, neural links, and HUD overrides for your operator profile."
                canonicalUrl="https://startzone.dev/profile/settings"
            />

            <aside className="fixed left-0 top-20 z-40 hidden h-[calc(100%-5rem)] w-64 flex-col border-r border-[#484848]/15 bg-[#0e0e0e] pb-8 pt-4 md:flex">
                <div className="mb-10 px-6">
                    <div className="mb-2 flex items-center gap-3 p-3 bg-[#131313] border border-[#484848]/10">
                        <div className="flex h-12 w-12 items-center justify-center bg-[#8cacff]/20">
                            <MaterialIcon icon="military_tech" size={24} fill className="text-[#8cacff]" />
                        </div>
                        <div>
                            <div className="font-['Space_Grotesk'] text-sm font-black uppercase tracking-tighter text-[#8cacff]">
                                LEVEL {level}
                            </div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-[#acabaa]">ELITE DEPLOYER</div>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="w-full border border-[#8cacff]/30 bg-[#8cacff]/10 py-2 font-['Space_Grotesk'] text-[10px] font-black uppercase tracking-[0.2em] text-[#8cacff]"
                    >
                        REDEEM XP
                    </button>
                </div>

                <nav className="flex-1 space-y-1">
                    {SYSTEM_NAV.map((item) => (
                        <Link
                            key={item.label}
                            to={item.path}
                            className={item.path === '/profile/settings'
                                ? "flex items-center gap-4 border-l-4 border-[#00cffc] bg-[#131313] py-3 pl-6 font-['Space_Grotesk'] text-sm font-bold uppercase text-[#00cffc]"
                                : "flex items-center gap-4 py-3 pl-6 font-['Space_Grotesk'] text-sm font-bold uppercase text-[#484848] transition-colors hover:bg-[#131313] hover:text-[#8cacff]"}
                        >
                            <MaterialIcon icon={item.icon} size={20} className="text-current" />
                            {item.label}
                        </Link>
                    ))}
                </nav>
            </aside>

            <main className="min-h-[calc(100vh-5rem)] bg-[#0e0e0e] px-4 pb-24 pt-8 md:ml-64 md:px-12">
                <div className="mx-auto max-w-5xl">
                    <header className="mb-12 border-l-4 border-[#00cffc] pl-6">
                        <h1 className="font-['Space_Grotesk'] text-5xl font-black uppercase tracking-tighter text-white">
                            SYSTEM CONFIG
                        </h1>
                        <p className="mt-2 font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.22em] text-[#767575]">
                            CORE ENGINE // USER UUID: {operatorId}
                        </p>
                    </header>

                    <div className="grid gap-8">
                        <section className="grid gap-4 md:grid-cols-3">
                            <div className="relative overflow-hidden bg-[#131313] p-8 md:col-span-2">
                                <div className="absolute right-0 top-0 p-4 opacity-10">
                                    <MaterialIcon icon="badge" size={80} className="text-[#8cacff]" />
                                </div>
                                <h2 className="mb-6 flex items-center gap-2 font-['Space_Grotesk'] text-xl font-bold uppercase tracking-[0.18em] text-[#8cacff]">
                                    <span className="h-2 w-2 bg-[#8cacff]" />
                                    Identity HUD
                                </h2>
                                <div className="grid gap-8 sm:grid-cols-2">
                                    <div>
                                        <div className="mb-1 font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.18em] text-[#767575]">
                                            Deployment Date
                                        </div>
                                        <div className="font-['Space_Grotesk'] text-xl font-black uppercase text-white">
                                            March 2024
                                        </div>
                                    </div>
                                    <div>
                                        <div className="mb-1 font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.18em] text-[#767575]">
                                            Security Clearance
                                        </div>
                                        <div className="font-['Space_Grotesk'] text-xl font-black uppercase text-[#00cffc]">
                                            Level {level}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col justify-center bg-[#1f2020] p-8 shadow-[inset_0_4px_0_0_rgba(140,172,255,0.7)]">
                                <div className="font-['Space_Grotesk'] text-4xl font-black text-[#8cacff]">
                                    {Math.max(totalXp, 8200).toLocaleString()}
                                </div>
                                <div className="mt-1 font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.18em] text-[#767575]">
                                    Total XP Earned
                                </div>
                                <div className="mt-4 h-1 bg-[#252626]">
                                    <div className="h-full w-3/4 bg-[#8cacff] shadow-[0_0_10px_rgba(140,172,255,0.45)]" />
                                </div>
                            </div>
                        </section>

                        <section className="bg-[#131313] p-8">
                            <h2 className="mb-8 flex items-center gap-2 font-['Space_Grotesk'] text-xl font-bold uppercase tracking-[0.18em] text-[#8cacff]">
                                <span className="h-2 w-2 bg-[#8cacff]" />
                                Theme Override
                            </h2>
                            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                                {THEME_OPTIONS.map((theme) => (
                                    <button
                                        key={theme.label}
                                        type="button"
                                        className="group flex flex-col items-center gap-3 bg-[#1f2020] p-4 text-left transition-all hover:scale-[1.01]"
                                        style={{
                                            border: theme.active ? `2px solid ${theme.accent}` : '1px solid rgba(72,72,72,0.25)',
                                            boxShadow: theme.active ? `0 0 15px ${theme.accent}40` : undefined,
                                        }}
                                    >
                                        <div
                                            className="flex h-12 w-full items-center justify-center"
                                            style={{
                                                backgroundColor: theme.swatch,
                                                boxShadow: `0 0 18px ${theme.swatch}55`,
                                            }}
                                        >
                                            {theme.active ? <MaterialIcon icon="check_circle" size={20} className="text-black" /> : null}
                                        </div>
                                        <div className="w-full text-center">
                                            <div
                                                className="mb-1 font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.18em]"
                                                style={{ color: theme.active ? theme.accent : '#767575' }}
                                            >
                                                {theme.meta}
                                            </div>
                                            <div className="font-['Space_Grotesk'] text-xs font-black uppercase tracking-[0.18em] text-white">
                                                {theme.label}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </section>

                        <section className="bg-[#131313] p-8">
                            <h2 className="mb-8 flex items-center gap-2 font-['Space_Grotesk'] text-xl font-bold uppercase tracking-[0.18em] text-[#8cacff]">
                                <span className="h-2 w-2 bg-[#8cacff]" />
                                Neural Links
                            </h2>
                            <div className="space-y-4">
                                {PROVIDERS.map((entry) => {
                                    const connected = user.provider === entry.provider;
                                    return (
                                        <div
                                            key={entry.provider}
                                            className="flex flex-col gap-4 bg-[#1f2020] p-4 transition-colors hover:bg-[#252626] sm:flex-row sm:items-center sm:justify-between"
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className="flex h-12 w-12 items-center justify-center bg-[#252626]">
                                                    <OAuthProviderIcon provider={entry.provider} className="h-6 w-6 text-white" />
                                                </div>
                                                <div>
                                                    <div className="font-['Space_Grotesk'] text-sm font-bold uppercase text-white">
                                                        {entry.label}
                                                    </div>
                                                    <div className="font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.18em] text-[#767575]">
                                                        Status:{' '}
                                                        <span className={connected ? 'text-[#00cffc]' : 'text-[#767575]'}>
                                                            {connected ? 'ESTABLISHED' : 'OFFLINE'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className={connected
                                                    ? 'border border-[#484848]/30 px-6 py-2 font-["Space_Grotesk"] text-[10px] font-black uppercase tracking-[0.18em] text-[#e8eaf0]'
                                                    : 'bg-[#00cffc] px-6 py-2 font-["Space_Grotesk"] text-[10px] font-black uppercase tracking-[0.18em] text-[#003a48]'}
                                            >
                                                {connected ? 'DISCONNECT' : 'CONNECT'}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>

                        <section className="mt-4">
                            <div className="relative overflow-hidden bg-[#9f0519]/10 p-8 shadow-[inset_0_0_0_1px_rgba(255,113,108,0.2)]">
                                <div
                                    className="pointer-events-none absolute inset-0 opacity-15"
                                    style={{
                                        background:
                                            'linear-gradient(to bottom, transparent 50%, rgba(255, 113, 108, 0.06) 50%)',
                                        backgroundSize: '100% 4px',
                                    }}
                                />
                                <div className="relative">
                                    <h2 className="mb-4 flex items-center gap-2 font-['Space_Grotesk'] text-xl font-bold uppercase tracking-[0.18em] text-[#ff716c]">
                                        <MaterialIcon icon="warning" size={18} className="text-current" />
                                        Void Protocol
                                    </h2>
                                    <p className="mb-8 max-w-2xl text-sm leading-7 text-[#ffa8a3]">
                                        Executing this command will result in permanent data erasure. All progress, XP, and neural links
                                        associated with <span className="font-['JetBrains_Mono'] font-bold text-[#ff716c]">{operatorId}</span>{' '}
                                        will be purged from the mainframe. This action cannot be undone.
                                    </p>
                                    <Link
                                        to="/contact"
                                        className="inline-flex items-center gap-3 bg-[#ff716c] px-8 py-4 font-['Space_Grotesk'] text-xs font-black uppercase tracking-[0.18em] text-[#490006] transition-all hover:brightness-110 active:scale-[0.99]"
                                    >
                                        <MaterialIcon icon="delete_forever" size={16} className="text-current" />
                                        TERMINATE ACCOUNT
                                    </Link>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </main>
        </div>
    );
}
